import React from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { resetPassword } from "../../services/auth";
// import BottomFooter from "../../components/BottomFooter";
import { toast } from "sonner";
import zxcvbn from "zxcvbn";

function ResetPassword() {
  const navigate = useNavigate();
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [searchParams] = useSearchParams();

  let userId = searchParams.get("userId") ?? "";
  let token = searchParams.get("token") ?? "";

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: resetPassword,
  });

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,

    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("Please Enter Your Password")
        .min(6, "Password must be at least 6 characters") // RequiredLength
        .matches(/[0-9]/, "Password must contain at least one digit") // RequireDigit
        .matches(/[a-z]/, "Password must contain at least one lowercase letter") // RequireLowercase
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter") // RequireUppercase
        .matches(
          /[^a-zA-Z0-9]/,
          "Password must contain at least one special character",
        ) // RequireNonAlphanumeric
        .test(
          "unique-chars",
          "Password must contain at least 4 unique characters",
          (value) => {
            if (!value) return false;

            const uniqueChars = new Set(value.split(""));
            return uniqueChars.size >= 4;
          },
        ), // RequiredUniqueChars
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values) => {
      let reqData = {
        userId: userId,
        token: token,
        newPassword: values.newPassword,
      };
      mutate(reqData, {
        onSuccess: (data) => {
          console.log(data?.message);
          toast.success(data?.message);

          navigate(`/auth/login?message=${data?.message}`, { replace: true });
        },
        onError: (error) => {
          console.log(error);
          toast.error("Unable to reset a user.");
        },
      });
    },
  });

  const passwordStrength = zxcvbn(validation.values.newPassword || "");

  const passwordChecks = {
    length: validation.values.newPassword.length >= 6,
    uppercase: /[A-Z]/.test(validation.values.newPassword),
    lowercase: /[a-z]/.test(validation.values.newPassword),
    digit: /[0-9]/.test(validation.values.newPassword),
    special: /[^a-zA-Z0-9]/.test(validation.values.newPassword),
    unique: new Set(validation.values.newPassword.split("")).size >= 4,
  };

  if (!userId || !token) {
    return <Navigate to={"/auth/login"} replace state={null} />;
  }

  return (
    <React.Fragment>
      <div className="auth-page-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-7 col-lg-7 col-xl-4">
              <div className="card mt-24 shadow-lg border-0">
                <div className="card-body p-24">
                  {isError && (
                    <div
                      className="alert alert-danger text-sm border-0"
                      role="alert"
                    >
                      {error}
                    </div>
                  )}

                  <div className="text-center mt-2">
                    <h6 className="text-dark mb-1">Reset Password</h6>
                    <p className="text-muted text-sm">
                      Reset your password to continue to phAMACart.
                    </p>
                  </div>
                  <div className="p-2 mt-24">
                    <form
                      action="#"
                      autoComplete="off"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-16">
                        <label className="form-label">Password</label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <input
                            type={isShowPassword ? "text" : "password"}
                            id="password"
                            name="newPassword"
                            value={validation.values.newPassword || ""}
                            className={`form-control pe-5 ${
                              validation.touched.newPassword &&
                              validation.errors.newPassword &&
                              "is-invalid"
                            }`}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                          <span
                            className={`toggle-password position-absolute top-50 inset-inline-end-0 me-30 translate-middle-y cursor-pointer`}
                            id="#enter-password"
                            onClick={() => setIsShowPassword(!isShowPassword)}
                          >
                            <i
                              className={`${isShowPassword ? "ph ph-eye-slash" : "ph ph-eye"}`}
                            />
                          </span>
                        </div>
                        {/* <p className="invalid-feedback">
                          {validation.errors.password}
                        </p> */}
                        {/* Strength Meter */}
                        {validation.errors.newPassword && (
                          <div className="mt-2">
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className={`progress-bar ${
                                  passwordStrength.score <= 1
                                    ? "bg-danger"
                                    : passwordStrength.score === 2
                                      ? "bg-warning"
                                      : passwordStrength.score >= 3
                                        ? "bg-success"
                                        : ""
                                }`}
                                role="progressbar"
                                style={{
                                  width: `${(passwordStrength.score + 1) * 20}%`,
                                }}
                              />
                            </div>

                            <small className="text-muted">
                              Strength:{" "}
                              {
                                ["Very Weak", "Weak", "Fair", "Good", "Strong"][
                                  passwordStrength.score
                                ]
                              }
                            </small>
                          </div>
                        )}
                        {/* Requirement Checklist */}
                        {validation.values.newPassword && (
                          <ul className="list-unstyled small mt-3 mb-0">
                            <li
                              className={
                                passwordChecks.length
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.length ? "✓" : "✗"} Minimum 6
                              characters
                            </li>

                            <li
                              className={
                                passwordChecks.uppercase
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.uppercase ? "✓" : "✗"} One
                              uppercase letter
                            </li>

                            <li
                              className={
                                passwordChecks.lowercase
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.lowercase ? "✓" : "✗"} One
                              lowercase letter
                            </li>

                            <li
                              className={
                                passwordChecks.digit
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.digit ? "✓" : "✗"} One number
                            </li>

                            <li
                              className={
                                passwordChecks.special
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.special ? "✓" : "✗"} One special
                              character
                            </li>

                            <li
                              className={
                                passwordChecks.unique
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {passwordChecks.unique ? "✓" : "✗"} 4 unique
                              characters
                            </li>
                          </ul>
                        )}
                        {/* zxcvbn feedback */}
                        {validation.values.newPassword &&
                          passwordStrength.feedback.warning && (
                            <div className="text-warning small mt-2">
                              {passwordStrength.feedback.warning}
                            </div>
                          )}
                      </div>
                      <div className="mb-16">
                        <label className="form-label">Confirm Password</label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <input
                            type={isShowPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={validation.values.confirmPassword || ""}
                            className={`form-control pe-5 ${
                              validation.touched.confirmPassword &&
                              validation.errors.confirmPassword &&
                              "is-invalid"
                            }`}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                          <span
                            className={`toggle-password position-absolute top-50 inset-inline-end-0 me-30 translate-middle-y cursor-pointer`}
                            id="#enter-password"
                            onClick={() => setIsShowPassword(!isShowPassword)}
                          >
                            <i
                              className={`${isShowPassword ? "ph ph-eye-slash" : "ph ph-eye"}`}
                            />
                          </span>
                        </div>
                        <p className="invalid-feedback">
                          {validation.errors.confirmPassword}
                        </p>
                      </div>

                      <div className="mb-16">
                        <p className="text-gray-500 text-xs">
                          Your personal data will be used to process your order,
                          support your experience throughout this website, and
                          for other purposes described in our
                          <Link
                            to="#"
                            className="text-main-600 text-decoration-underline"
                          >
                            {" "}
                            privacy policy
                          </Link>
                          .
                        </p>
                      </div>
                      <div className="mt-24">
                        <button
                          type="submit"
                          className="btn btn-success w-100"
                          disabled={isPending}
                        >
                          {isPending && (
                            <div
                              className="spinner-border spinner-border-sm me-5"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          )}
                          Reset Password
                        </button>
                      </div>

                      <div className="mt-24 text-center">
                        <p className="mb-0">
                          Already have an account ?{" "}
                          <Link
                            className="fw-semibold text-primary text-decoration-underline"
                            to="/auth/login"
                            data-discover="true"
                          >
                            {" "}
                            Signin{" "}
                          </Link>{" "}
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* BottomFooter */}
      {/* <div id="footer">
        <BottomFooter />
      </div> */}
    </React.Fragment>
  );
}

export default ResetPassword;
